import { Routes } from "@angular/router";
import { AuthGuard } from "./services/guard/auth.guard";
import { groupLandingGuard } from "./services/guard/group-landing.guard";
import { surveyCompletionGuard } from "./services/guard/survey-completion.guard";
import { Role } from "./shared/utils/role.model";
import { MERCHANT_DASHBOARD_ROLES } from "./shared/utils/merchant-dashboard-roles";
import {
  MERCHANT_MODE,
  METRICS_MODE,
} from "./features/solution/dashboard-admin/metrics-mode";
import {
  LegacyRouteRedirectGuard,
  legacyRouteMatcher,
} from "./shared/utils/legacy-route-redirect";

const guarded = (groups?: string[]) => ({
  canActivate: [AuthGuard, surveyCompletionGuard],
  runGuardsAndResolvers: "always" as const,
  ...(groups ? { data: { groups } } : {}),
});

export const routes: Routes = [
  {
    path: "public",
    loadChildren: () =>
      import("./features/public/public.module").then((m) => m.PublicModule),
  },
  {
    // Developer API documentation portal. Public and guard-free: every page is written
    // content bundled with the app, so it fetches nothing. The only request a reader can
    // trigger is the one they run themselves in the Try It console, with a token they
    // paste in.
    path: "docs",
    loadChildren: () =>
      import("./features/api-docs/api-docs.routes").then((m) => m.API_DOCS_ROUTES),
  },
  {
    // A merchant whose sign-up questionnaire was never answered (they closed the tab, or
    // logged back in later) is sent here by surveyCompletionGuard before reaching anything
    // else — roles/access groups are purely selection-driven now, so there is nothing usable
    // to show them until it's answered. AuthGuard only (not surveyCompletionGuard itself,
    // which would redirect this page into a loop).
    path: "complete-profile",
    canActivate: [AuthGuard],
    runGuardsAndResolvers: "always",
    loadComponent: () =>
      import("./features/complete-profile/complete-profile.component").then(
        (m) => m.CompleteProfileComponent,
      ),
  },
  {
    path: "dashboard/stats",
    pathMatch: "full",
    redirectTo: "dashboard",
  },
  {
    // Merchant analytics dashboard: the platform metrics dashboard run in merchant mode (company
    // from the JWT). Open to anyone holding one of the roles of a dashboard section; the page
    // itself shows only the sections the API grants.
    path: "dashboard",
    loadComponent: () =>
      import("./features/solution/dashboard-admin/dashboard-admin.component").then(
        (m) => m.DashboardAdminComponent,
      ),
    providers: [{ provide: METRICS_MODE, useValue: MERCHANT_MODE }],
    canActivate: [AuthGuard, surveyCompletionGuard],
    runGuardsAndResolvers: "always",
    data: { roles: [...MERCHANT_DASHBOARD_ROLES], roleMode: "any" },
  },
  {
    // Previous dashboard (analytics chat), kept reachable while the new one rolls out.
    path: "dashboard-v0",
    loadChildren: () =>
      import("./features/analytics-chat/analytics-chat.module").then(
        (m) => m.AnalyticsChatModule,
      ),
    ...guarded(),
  },

  // Full-page onboarding checklist (temporary entry point — see the sidebar's
  // onboarding section; both go away once onboarding moves server-side).
  {
    path: "onboarding",
    loadComponent: () =>
      import(
        "./features/onboarding/pages/onboarding-checklist-page/onboarding-checklist-page.component"
      ).then((m) => m.OnboardingChecklistPageComponent),
    ...guarded(),
  },

  // Every import of the company; each service only lists the kinds of records the user may import.
  {
    path: "imports",
    loadComponent: () =>
      import("./features/imports/import-history-page/import-history-page.component").then(
        (m) => m.ImportHistoryPageComponent,
      ),
    ...guarded(),
  },

  {
    path: "messaging/inbox",
    loadChildren: () =>
      import("./features/team-inbox/team-inbox.module").then(
        (m) => m.TeamInboxModule,
      ),
    ...guarded(["/MESSAGING"]),
  },
  {
    // Must be declared before "messaging/contacts" below: that route's path is matched as a
    // prefix (loadChildren + no pathMatch), so it would otherwise swallow these two first and
    // they'd never be reached.
    path: "messaging/contacts/lists",
    loadComponent: () =>
      import("./features/contact/contact-lists-page/contact-lists-page.component").then(
        (m) => m.ContactListsPageComponent,
      ),
    // Contacts belong to catalog/e-commerce merchants too, not just WhatsApp — see menu-tree.ts.
    ...guarded(["/MESSAGING", "/ORDER"]),
  },
  {
    path: "messaging/contacts/segments",
    loadComponent: () =>
      import("./features/contact/contact-segments-page/contact-segments-page.component").then(
        (m) => m.ContactSegmentsPageComponent,
      ),
    ...guarded(["/MESSAGING", "/ORDER"]),
  },
  {
    path: "messaging/contacts",
    loadChildren: () =>
      import("./features/contact/contact.module").then((m) => m.ContactModule),
    // Contacts belong to catalog/e-commerce merchants too, not just WhatsApp — see menu-tree.ts.
    ...guarded(["/MESSAGING", "/ORDER"]),
  },
  {
    path: "messaging/lists",
    loadChildren: () =>
      import("./features/list/list.module").then((m) => m.ListModule),
    ...guarded(["/MESSAGING"]),
  },
  {
    path: "messaging/segments",
    loadChildren: () =>
      import("./features/segment/segment.module").then((m) => m.SegmentModule),
    ...guarded(["/MESSAGING"]),
  },
  {
    path: "messaging/tickets",
    loadChildren: () =>
      import("./features/manage-ticket/manage-ticket.module").then(
        (m) => m.ManageTicketModule,
      ),
    ...guarded(),
  },

  {
    path: "catalog/whatsapp-storefront",
    loadComponent: () =>
      import("./features/catalog-storefront/whatsapp-storefront-page.component").then(
        (m) => m.WhatsappStorefrontPageComponent,
      ),
    ...guarded(["/ORDER"]),
    data: {
      groups: ["/ORDER"],
      roles: [Role.COMPANY_MANAGE, Role.CATALOG_SUBSCRIPTION],
      roleMode: "any",
    },
  },
  {
    // Restaurant tables, one QR code per table (storefront feature, next to the Vitrine).
    path: "catalog/tables",
    children: [
      {
        path: "",
        loadComponent: () =>
          import("./features/catalog-storefront/dining-tables/dining-tables-page.component").then(
            (m) => m.DiningTablesPageComponent,
          ),
      },
      {
        path: "print",
        loadComponent: () =>
          import(
            "./features/catalog-storefront/dining-tables/dining-tables-print-page/dining-tables-print-page.component"
          ).then((m) => m.DiningTablesPrintPageComponent),
      },
    ],
    ...guarded(["/ORDER"]),
    data: {
      groups: ["/ORDER"],
      roles: [Role.COMPANY_MANAGE, Role.CATALOG_SUBSCRIPTION],
      roleMode: "any",
    },
  },
  {
    path: "catalog/discount-codes",
    loadComponent: () =>
      import("./features/catalog-discounts/discount-codes-page.component").then(
        (m) => m.DiscountCodesPageComponent,
      ),
    ...guarded(["/ORDER"]),
    data: {
      groups: ["/ORDER"],
      roles: [Role.COMPANY_MANAGE, Role.CATALOG_SUBSCRIPTION],
      roleMode: "any",
    },
  },
  {
    path: "catalog/orders",
    loadChildren: () =>
      import("./features/order/order.module").then((m) => m.OrderModule),
    ...guarded(["/ORDER"]),
  },
  {
    path: "catalog/reservations",
    loadChildren: () =>
      import("./features/reservation/reservation.module").then(
        (m) => m.ReservationModule,
      ),
    ...guarded(["/ORDER"]),
  },
  {
    path: "catalog/products",
    loadChildren: () =>
      import("./features/product/product.module").then((m) => m.ProductModule),
    ...guarded(["/ORDER"]),
  },
  {
    path: "catalog/categories",
    children: [
      {
        path: "",
        loadComponent: () =>
          import(
            "./features/product/category/category-page/category-page.component"
          ).then((m) => m.CategoryPageComponent),
      },
      {
        path: "archived",
        loadComponent: () =>
          import(
            "./features/product/category/category-archived-page/category-archived-page.component"
          ).then((m) => m.CategoryArchivedPageComponent),
      },
      {
        path: "new",
        canActivate: [AuthGuard],
        data: { roles: [Role.PRODUCT] },
        loadComponent: () =>
          import(
            "./components/product/category/category-form/category-form.component"
          ).then((m) => m.CategoryFormComponent),
      },
      {
        path: ":ref/edit",
        canActivate: [AuthGuard],
        data: { roles: [Role.PRODUCT] },
        loadComponent: () =>
          import(
            "./components/product/category/category-form/category-form.component"
          ).then((m) => m.CategoryFormComponent),
      },
    ],
    ...guarded(),
    data: {
      groups: ["/ORDER"],
      roles: [Role.PRODUCT, Role.PRODUCT_VIEW],
      roleMode: "any",
    },
  },
  {
    path: "catalog/vouchers",
    loadChildren: () =>
      import("./features/voucher/voucher.module").then((m) => m.VoucherModule),
    ...guarded(["/VOUCHER"]),
  },
  {
    path: "catalog/delivery",
    loadChildren: () =>
      import("./features/delivery/delivery.module").then(
        (m) => m.DeliveryModule,
      ),
    ...guarded(["/DELIVERY"]),
  },

  {
    path: "",
    pathMatch: "full",
    canActivate: [AuthGuard, surveyCompletionGuard, groupLandingGuard],
    loadComponent: () =>
      import("./shared/utils/landing-placeholder.component").then(
        (m) => m.LandingPlaceholderComponent,
      ),
  },

  // Fixed referential URLs must be evaluated before workflow `:ref` routes.
  {
    path: "",
    loadChildren: () =>
      import("./features/referential/referential.module").then(
        (m) => m.ReferentialModule,
      ),
    ...guarded(),
  },

  {
    path: "workflow",
    loadChildren: () =>
      import("./features/template/template.module").then(
        (m) => m.TemplateModule,
      ),
    ...guarded(["/MESSAGING", "/SOLUTION"]),
  },
  {
    path: "workflow",
    loadChildren: () =>
      import("./features/automations/automations.module").then(
        (m) => m.AutomationsModule,
      ),
    ...guarded(["/MESSAGING"]),
  },
  {
    path: "campaigns",
    loadChildren: () =>
      import("./features/broadcast/broadcast.module").then(
        (m) => m.BroadcastModule,
      ),
    ...guarded(["/MESSAGING"]),
  },
  {
    path: "payment",
    loadChildren: () =>
      import("./features/payment/payment.module").then((m) => m.PaymentModule),
    ...guarded(["/PAYMENT"]),
  },

  {
    path: "settings/catalog-integration",
    loadComponent: () =>
      import("./features/product/crm/crm.component").then(
        (m) => m.CrmComponent,
      ),
    ...guarded(),
    // Shops/ERPs for product managers, Zoho CRM for messaging automations: the page shows each its own cards.
    data: {
      groups: ["/ORDER", "/MESSAGING"],
      roles: [Role.PRODUCT, Role.AUTOMATION_DEF_ACTIONS, Role.WORKFLOW_VIEW],
      roleMode: "any",
    },
  },
  {
    path: "settings/catalog-integration/products",
    loadComponent: () =>
      import("./features/product/crm-products/crm-products.component").then(
        (m) => m.CrmProductsComponent,
      ),
    ...guarded(),
    data: { groups: ["/ORDER"], roles: [Role.PRODUCT] },
  },
  {
    path: "settings/notifications",
    loadChildren: () =>
      import("./features/automations/notifications.module").then(
        (m) => m.NotificationsModule,
      ),
    ...guarded(["/ADMINISTRATION", "/ORDER"]),
  },
  {
    path: "settings",
    loadChildren: () =>
      import("./features/administration/administration.module").then(
        (m) => m.AdministrationModule,
      ),
    // /MESSAGING added alongside /ADMINISTRATION and /ORDER: a WhatsApp-only self-service
    // merchant holds COMPANY_MANAGE directly (see CompanyService.ADMINISTRATION_BASELINE_ROLES)
    // but was never joined to the real /ADMINISTRATION Keycloak group on purpose — that group's
    // live content leaks read/write access to every other service, unrelated to what they asked
    // for. Without this, they'd hold the role but never be able to reach the page that uses it.
    ...guarded(["/ADMINISTRATION", "/ORDER", "/MESSAGING"]),
    data: {
      groups: ["/ADMINISTRATION", "/ORDER", "/MESSAGING"],
      groupMode: "any",
    },
  },
  {
    path: "platform",
    loadChildren: () =>
      import("./features/solution/solution.module").then(
        (m) => m.SolutionModule,
      ),
    ...guarded(["/SOLUTION"]),
  },

  {
    matcher: legacyRouteMatcher,
    canActivate: [LegacyRouteRedirectGuard],
    loadComponent: () =>
      import("./shared/not-found-view/route-not-found-page.component").then(
        (m) => m.RouteNotFoundPageComponent,
      ),
  },
  {
    path: "not-found",
    loadComponent: () =>
      import("./shared/not-found-view/route-not-found-page.component").then(
        (m) => m.RouteNotFoundPageComponent,
      ),
  },
  {
    path: "**",
    redirectTo: "not-found",
  },
];
